import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { tenantApiFetch } from './tenantApi';
import { useTenantAuth } from '../context/TenantAuthContext';

function statusColor(s) {
  if (s === 'active') return 'success';
  if (s === 'failed') return 'error';
  return 'default';
}

export default function TenantSessionDetailPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { hasPermission, refreshMe } = useTenantAuth();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [terminating, setTerminating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/login-sessions/${sessionId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setEntry(json.entry);
    } catch (e) {
      setError(e.message);
      setEntry(null);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const doTerminate = async () => {
    setTerminating(true);
    try {
      const res = await tenantApiFetch(`/api/tenant/login-sessions/${sessionId}/terminate`, {
        method: 'POST',
        body: {}
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Terminate failed');
      setConfirmOpen(false);
      await load();
      await refreshMe();
    } catch (e) {
      setError(e.message);
    } finally {
      setTerminating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !entry) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!entry) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
        {value ?? '—'}
      </Typography>
    </Grid>
  );

  const canTerminate = hasPermission('tenant.sessions.terminate') && entry.status === 'active';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Session #{entry.id}</Typography>
          <Box sx={{ mt: 1 }}>
            <Chip size="small" label={entry.status} color={statusColor(entry.status)} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canTerminate && (
            <Button variant="outlined" color="warning" onClick={() => setConfirmOpen(true)}>
              End session
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate('/tenant/sessions')}>
            Back to list
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {row('Tenant ID', entry.tenant_id)}
          {row('User ID', entry.user_id)}
          {row('User email', entry.user_email)}
          {row('Login at', entry.login_at ? String(entry.login_at) : '—')}
          {row('Logout at', entry.logout_at ? String(entry.logout_at) : '—')}
          {row('Last activity', entry.last_activity_at ? String(entry.last_activity_at) : '—')}
          {row('IP (masked)', entry.ip_address)}
          {row('User agent', entry.user_agent)}
          {row('Auth method', entry.auth_method)}
          {row('Failure reason', entry.failure_reason)}
          {row('Created at', entry.created_at ? String(entry.created_at) : '—')}
        </Grid>
      </Paper>

      <Typography variant="body2" color="text.secondary">
        Ending a session marks it closed; the bearer token will be rejected on the next API call when session binding is
        enabled for that login.
      </Typography>

      <Dialog open={confirmOpen} onClose={() => !terminating && setConfirmOpen(false)}>
        <DialogTitle>End this session?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The user must sign in again. If this is your own session, you will be signed out after the next request.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={terminating}>
            Cancel
          </Button>
          <Button onClick={doTerminate} color="warning" variant="contained" disabled={terminating}>
            {terminating ? 'Working…' : 'End session'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
